// Register list derivations (pure, no React), shared with the registration
// page (pages/registration/derive.ts): prices, dates in the payload's zone
// (the league's game zone, else the viewer's) and the card view model.

import { usd } from '@/site/kit/forms/fields'
import type { MemberRegistrationCard } from '@/services/memberApi'
import type { UiStrings } from '../../labels/ui'
import type { RegistrationCardVM } from './types'

export type RegistrationUi = UiStrings['registration']

export function priceText(feeCents: number, ui: RegistrationUi): string {
  return !feeCents ? ui.free : usd(feeCents)
}

function formatIn(iso: string | null | undefined, tz: string | null | undefined, opts: Intl.DateTimeFormatOptions): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (isNaN(d.getTime())) return null
  try {
    return d.toLocaleString('en-US', tz ? { ...opts, timeZone: tz } : opts)
  } catch {
    // Unknown IANA zone: fall back to the viewer's.
    return d.toLocaleString('en-US', opts)
  }
}

/** "Mon, Mar 2, 9:00 AM EST" in `tz` (the viewer's zone when null or unknown). */
export function instantText(iso: string | null | undefined, tz: string | null | undefined): string | null {
  return formatIn(iso, tz, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })
}

/** "Fri, Oct 16" in `tz`. */
export function dayText(iso: string | null | undefined, tz: string | null | undefined): string | null {
  return formatIn(iso, tz, { weekday: 'short', month: 'short', day: 'numeric' })
}

/** "Mar 2, 2027" in `tz`. */
export function dateText(iso: string | null | undefined, tz: string | null | undefined): string | null {
  return formatIn(iso, tz, { month: 'short', day: 'numeric', year: 'numeric' })
}

export function spotsText(spotsLeft: number | null, ui: RegistrationUi): string | null {
  if (spotsLeft === null || spotsLeft === undefined) return null
  return spotsLeft <= 0 ? ui.full : ui.spotsLeft(spotsLeft)
}

/** Opening is still ahead (a future `opens_at`). */
export function opensAhead(iso: string | null, now = Date.now()): boolean {
  if (!iso) return false
  const t = Date.parse(iso)
  return !isNaN(t) && t > now
}

export const registrationHref = (slug: string): string => `/register/${encodeURIComponent(slug)}`

/**
 * One Register list card. A team registration's capacity counts teams: the
 * server's `spots_left` counts the teams holding a place there (owner audit,
 * October 29; it used to subtract players), and the card shows only "Full",
 * as the registration page's facts do (pages/registration/derive.ts ›
 * factsVM), never "N spots left" that a visitor would read as players.
 * A full registration with its waitlist on (the server's `waitlist` state)
 * can still be joined, so its card says so instead of "View details".
 */
export function cardVM(card: MemberRegistrationCard, tz: string | null, ui: RegistrationUi, now = Date.now()): RegistrationCardVM {
  const ahead = opensAhead(card.opens_at, now)
  const opens = ahead ? instantText(card.opens_at, tz) : null
  const closes = instantText(card.closes_at, tz)
  const waitlist = card.sign_up?.state === 'waitlist'
  const full = card.sign_up?.state === 'full' || waitlist || card.spots_left === 0
  const team = card.participant === 'team'
  return {
    key: card.slug,
    href: registrationHref(card.slug),
    name: card.name,
    seasonLabel: card.season?.label ?? '',
    priceLabel: priceText(card.fee_cents, ui),
    opensLabel: opens ? ui.opens(opens) : null,
    closesLabel: closes ? ui.closes(closes) : null,
    spotsLabel: team ? (full ? ui.full : null) : spotsText(card.spots_left, ui),
    full,
    // An upcoming registration (Website › Design › Registration's "Show
    // upcoming registrations") can't be joined yet: its card only shows it.
    actionLabel: waitlist ? ui.joinWaitlist : full || ahead ? ui.viewDetails : ui.viewAction,
  }
}
